import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as authApi from '../api/auth';
import { setOnSessionExpired } from '../api/client';
import type { AuthUser } from '../api/auth';

export interface AuthContextValue {
  user: AuthUser | null;
  /** True until the initial silent refresh attempt settles. */
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  /** Creates the (unverified) account; the session arrives via verifyEmail. */
  register: (email: string, password: string, display_name: string) => Promise<{ email: string }>;
  verifyEmail: (email: string, code: string) => Promise<AuthUser>;
  loginWithGoogleCode: (
    code: string,
    verifier: string | undefined,
    redirectUri: string,
  ) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<AuthUser | null>;
  /** Used by settings/profile flows after server-side profile changes. */
  setUser: (user: AuthUser | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Silent resume: the httpOnly cookie may hold a valid refresh token.
    let cancelled = false;
    authApi
      .tryRefresh()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    setOnSessionExpired(() => setUser(null));
    return () => {
      cancelled = true;
      setOnSessionExpired(null);
    };
  }, []);

  // Every cached query was fetched AS somebody — `['me','today']`, the
  // leaderboard's own-rank row, a profile, a season standing. React Query
  // keys them by name, not by viewer, so without this the next person to sign
  // in on this device is served the previous one's data until it refetches.
  // Signing out and back in on a shared browser is exactly the flow the guest
  // gate encourages, so this runs on every identity change.
  //
  // Drops everything EXCEPT the dictionary, which is large, static and
  // identical for everyone. Safe-by-default on purpose: a query added later
  // is cleared unless someone deliberately exempts it.
  const resetViewerCache = useCallback(() => {
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'dictionary' });
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string) => {
      const u = await authApi.login(email, password);
      resetViewerCache();
      setUser(u);
      return u;
    },
    [resetViewerCache],
  );

  const register = useCallback(
    (email: string, password: string, display_name: string) =>
      authApi.register(email, password, display_name),
    [],
  );

  const verifyEmail = useCallback(
    async (email: string, code: string) => {
      const u = await authApi.verifyEmail(email, code);
      resetViewerCache();
      setUser(u);
      return u;
    },
    [resetViewerCache],
  );

  const loginWithGoogleCode = useCallback(
    async (code: string, verifier: string | undefined, redirectUri: string) => {
      const u = await authApi.googleCallback(code, verifier, redirectUri);
      resetViewerCache();
      setUser(u);
      return u;
    },
    [resetViewerCache],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    resetViewerCache();
    setUser(null);
  }, [resetViewerCache]);

  const refresh = useCallback(async () => {
    const u = await authApi.tryRefresh();
    setUser(u);
    return u;
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      register,
      verifyEmail,
      loginWithGoogleCode,
      logout,
      refresh,
      setUser,
    }),
    [user, loading, login, register, verifyEmail, loginWithGoogleCode, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
